import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface BillingProfile {
  businessName: string;
  billingEmail: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  taxId: string;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export interface Invoice {
  id: string;
  date: string;
  invoiceNumber: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed';
  downloadUrl?: string;
}

export interface UsageCharge {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export const billingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBillingProfile: builder.query<BillingProfile, void>({
      query: () => '/portal/billing/profile',
      transformResponse: (response: unknown) => normalizeKeys<BillingProfile>(response),
      providesTags: ['Billing'],
    }),

    updateBillingProfile: builder.mutation<BillingProfile, Partial<BillingProfile>>({
      query: (body) => ({
        url: '/portal/billing/profile',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Billing'],
    }),

    getPaymentMethods: builder.query<PaymentMethod[], void>({
      query: () => '/portal/billing/payment-methods',
      transformResponse: (response: unknown) => normalizeKeys<PaymentMethod[]>(response),
    }),

    getInvoices: builder.query<Invoice[], void>({
      query: () => '/portal/billing/invoices',
      transformResponse: (response: unknown) => normalizeKeys<Invoice[]>(response),
      providesTags: ['Invoice'],
    }),

    getCurrentUsage: builder.query<UsageCharge[], void>({
      query: () => '/portal/billing/usage',
      transformResponse: (response: unknown) => normalizeKeys<UsageCharge[]>(response),
    }),
  }),
});

export const {
  useGetBillingProfileQuery,
  useUpdateBillingProfileMutation,
  useGetPaymentMethodsQuery,
  useGetInvoicesQuery,
  useGetCurrentUsageQuery,
} = billingApi;
